import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Package, Calculator, ArrowRight, Ruler, Plane, PackageCheck, Gift, ShieldCheck, Ship } from 'lucide-react';
import { BuyoutCalculatorModal, ShippingCalculatorModal } from './CalculatorModals';
import { ShipmentSchedule } from './ShipmentSchedule';

const UTM_HEADLINES = {
  china: 'Швидка та надійна доставка вантажів з Китаю в Україну',
  vykup: 'Професійний викуп та перевірка товарів з 1688, Taobao та Alibaba',
  cargo: 'Карго з Китаю під ключ: авіа та море за найкращими тарифами',
  search: 'Пошук та аудит надійних постачальників у Китаї для вашого бізнесу',
};

const getHeadline = () => {
  try {
    const utmContent = new URLSearchParams(window.location.search).get('utm_content');
    return UTM_HEADLINES[utmContent] || 'Доставка товарів з Китаю — швидко та надійно';
  } catch {
    return 'Доставка товарів з Китаю — швидко та надійно';
  }
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------------- Service slider (left column) ---------------- */
const SLIDES = [
  { img: '/hero/slide1_air.jpg', tag: 'Авіадоставка', text: 'Авіа з Китаю: від 15–20 днів', icon: Plane, href: '/pravyla-perevezen', price: 'від 9 $/кг' },
  { img: '/hero/slide2_buyout.jpg', tag: 'Викуп товарів', text: 'Викуп з 1688, Taobao, Alibaba під ключ', icon: PackageCheck, href: '/vykup-tovariv-z-kytayu' },
  { img: '/hero/slide3_loyalty.jpg', tag: 'Програма лояльності', text: 'Кешбек та спеціальні бонуси для постійних клієнтів', icon: Gift, href: '/register' },
  { img: '/hero/slide4_insurance.jpg', tag: 'Страхування', text: '100% фінансова гарантія та страхування вантажу', icon: ShieldCheck, href: '/strakhuvannya-vantazhu' },
  { img: '/hero/slide5_sea.jpg', tag: 'Морська доставка', text: 'Море: оптимальний тариф для великих вантажів', icon: Ship, href: '/dostavka-zbirnyh-vantazhiv', price: 'від 1,9 $/кг' },
];
const SLIDE_MS = 5000;

const HeroSlider = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => setActive((a) => (a + 1) % SLIDES.length), SLIDE_MS);
    return () => clearTimeout(t);
  }, [active, paused]);

  return (
    <div
      className="relative w-full aspect-[16/11] sm:aspect-[16/9] lg:aspect-[5/6] rounded-2xl sm:rounded-3xl overflow-hidden border border-white/10 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.8)] bg-[#04060d]"
      data-testid="hero-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {SLIDES.map((s, i) => {
        const Icon = s.icon;
        return (
          <Link
            key={i}
            to={s.href}
            data-testid={`hero-slide-link-${i}`}
            className={`group absolute inset-0 transition-opacity duration-700 ease-out ${i === active ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}
            aria-hidden={i !== active}
            tabIndex={i === active ? 0 : -1}
          >
            <img
              src={s.img}
              alt={s.tag}
              loading={i === 0 ? 'eager' : 'lazy'}
              className={`w-full h-full object-cover transition-transform ease-out ${i === active ? 'scale-110' : 'scale-100'}`}
              style={{ transitionDuration: `${SLIDE_MS + 500}ms` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#02040a] via-[#02040a]/45 to-transparent" />
            {s.price && (
              <div className="absolute top-4 right-4 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-[#1a0a00] text-sm sm:text-base font-extrabold shadow-[0_0_20px_rgba(251,146,60,0.6)]" data-testid={`hero-slide-price-${i}`}>
                {s.price}
              </div>
            )}
            <div className="absolute left-5 right-5 bottom-12 sm:bottom-14">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/20 border border-orange-400/40 backdrop-blur-sm mb-3">
                <Icon className="w-4 h-4 text-orange-300" />
                <span className="text-orange-200 text-[11px] sm:text-xs font-semibold uppercase tracking-wide">{s.tag}</span>
              </div>
              <p className="text-white text-lg sm:text-2xl font-extrabold leading-snug drop-shadow-lg max-w-md break-anywhere">
                {s.text}
              </p>
              <span className="inline-flex items-center gap-1 mt-2 text-white/85 text-xs sm:text-sm font-semibold group-hover:text-orange-300 transition-colors">
                Дізнатися більше
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </div>
          </Link>
        );
      })}

      {/* Instagram-stories style progress indicators (tap to switch) */}
      <div className="absolute left-5 right-5 bottom-4 z-20 flex gap-2">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActive(i); }}
            className="flex-1 h-6 flex items-center cursor-pointer tap-sm"
            aria-label={`Слайд ${i + 1}`}
            data-testid={`hero-slide-dot-${i}`}
          >
            <span className="block w-full h-1.5 rounded-full bg-white/25 overflow-hidden">
              <span
                key={`${i}-${active}-${paused}`}
                className="block h-full rounded-full bg-gradient-to-r from-yellow-400 to-orange-500"
                style={
                  i < active
                    ? { width: '100%' }
                    : i === active
                      ? { animation: `storyFill ${SLIDE_MS}ms linear forwards`, animationPlayState: paused ? 'paused' : 'running' }
                      : { width: '0%' }
                }
              />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

/* ---------------- Hero ---------------- */
export const Hero = () => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [showShippingCalc, setShowShippingCalc] = useState(false);
  const [showBuyoutCalc, setShowBuyoutCalc] = useState(false);
  const trackingInputRef = useRef(null);

  const statusColors = {
    new: 'bg-blue-500', checking: 'bg-yellow-500', confirmed: 'bg-green-500',
    awaiting_payment: 'bg-red-500', in_transit: 'bg-purple-500',
    warehouse: 'bg-orange-500', delivered: 'bg-emerald-500',
  };

  const trackParcel = async () => {
    if (!trackingNumber.trim()) {
      toast.error('Введіть номер ТТН або номер посилки');
      return;
    }
    setTrackingLoading(true);
    try {
      const response = await axios.get(`${API}/track/${trackingNumber.trim()}`);
      setTrackingResult(response.data);
      if (!response.data.found) toast.error('Посилку не знайдено');
    } catch (error) {
      toast.error('Помилка пошуку');
      setTrackingResult(null);
    } finally {
      setTrackingLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const btnClass = 'h-14 px-6 text-base md:text-lg font-extrabold rounded-full border-0 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-[#1a0a00] shadow-[0_0_25px_rgba(251,146,60,0.5)] hover:shadow-[0_0_45px_rgba(251,146,60,0.9)] hover:scale-[1.03] hover:from-yellow-300 hover:via-orange-400 hover:to-red-400 transition-all';

  return (
    <section className="relative pt-5 md:pt-6 pb-10 overflow-hidden bg-[#02040a]">
      <div className="relative mx-3 sm:mx-6 lg:mx-10 rounded-3xl border border-yellow-400/40 shadow-[0_0_45px_-12px_rgba(250,204,21,0.35)] bg-[#050508] overflow-hidden pt-8 pb-10 md:pt-14 md:pb-16 px-4 sm:px-6 lg:px-10">
        {/* clean deep backdrop — subtle dot grid + soft glows, no parcel props */}
        <div className="absolute inset-0 pointer-events-none opacity-70" style={{ backgroundImage: 'radial-gradient(rgba(148,163,184,0.10) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="absolute -right-40 -top-40 w-96 h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(250,204,21,0.10) 0%, transparent 65%)' }} />
        <div className="absolute -left-40 -bottom-40 w-96 h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.10) 0%, transparent 65%)' }} />

        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* LEFT — slider */}
          <div className="order-1 animate-fadeIn">
            <HeroSlider />
          </div>

          {/* RIGHT — text + actions + schedule + tracking */}
          <div className="order-2">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight animate-fadeIn" data-testid="hero-headline">
              {getHeadline()}
            </h1>
            <p className="text-base sm:text-lg text-blue-100 mb-7 leading-relaxed animate-fadeIn" style={{ animationDelay: '0.15s' }}>
              Повний цикл логістики: викуп, консолідація, доставка до дверей.
              Працюємо з 2019 року. Понад 50 000 успішних доставок.
            </p>

            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 animate-fadeIn" style={{ animationDelay: '0.3s' }}>
              <Link to="/register" className="sm:flex-1">
                <Button size="lg" className={`${btnClass} w-full`} data-testid="hero-register-btn">
                  <ArrowRight className="w-5 h-5 mr-2" />
                  Почати роботу
                </Button>
              </Link>
              <Button size="lg" onClick={() => setShowBuyoutCalc(true)} className={`${btnClass} sm:flex-1`} data-testid="hero-calculator-btn">
                <Calculator className="w-5 h-5 mr-2" />
                Розрахувати викуп
              </Button>
              <Button size="lg" onClick={() => setShowShippingCalc(true)} className={`${btnClass} sm:flex-1`} data-testid="hero-shipping-calc-btn">
                <Ruler className="w-5 h-5 mr-2" />
                Вартість доставки
              </Button>
            </div>

            <ShipmentSchedule />

            <ShippingCalculatorModal open={showShippingCalc} onClose={() => setShowShippingCalc(false)} />
            <BuyoutCalculatorModal open={showBuyoutCalc} onClose={() => setShowBuyoutCalc(false)} />

            {/* Tracking */}
            <div className="mt-8 animate-fadeIn" style={{ animationDelay: '0.45s' }}>
              <p className="text-white/80 text-sm mb-3 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Відстежити посилку
              </p>
              <div className="tracking-wrap flex gap-2 rounded-lg">
                <Input
                  ref={trackingInputRef}
                  id="hero-tracking-input"
                  type="text"
                  placeholder="Номер ТТН або CAGS-XXXX"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && trackParcel()}
                  className="h-12 bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:bg-white/20 focus:border-white/50 min-w-0 flex-1"
                  data-testid="hero-tracking-input"
                />
                <Button
                  onClick={trackParcel}
                  disabled={trackingLoading || !trackingNumber.trim()}
                  className="h-12 px-6 bg-white/20 hover:bg-white/30 border border-white/30 text-white flex-shrink-0"
                  data-testid="hero-track-btn"
                >
                  {trackingLoading ? '...' : 'Знайти'}
                </Button>
              </div>

              {trackingResult && trackingResult.found && (
                <div className="mt-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 animate-fadeIn">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-white/70 text-xs">Посилка</p>
                      <p className="text-white font-bold break-anywhere">{trackingResult.internal_number}</p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-white text-sm font-medium flex-shrink-0 ${statusColors[trackingResult.status]}`}>
                      {trackingResult.status_label}
                    </div>
                  </div>
                  {trackingResult.status_dates && (
                    <div className="mt-3 pt-3 border-t border-white/20">
                      <p className="text-white/70 text-xs mb-2">Останнє оновлення</p>
                      <p className="text-white text-sm">{formatDate(trackingResult.status_dates[trackingResult.status])}</p>
                    </div>
                  )}
                </div>
              )}

              {trackingResult && !trackingResult.found && (
                <div className="mt-4 p-3 bg-red-500/20 backdrop-blur-sm rounded-lg border border-red-400/30">
                  <p className="text-white text-sm">Посилку не знайдено. Перевірте номер.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
